const measurements = new Map();

export function findLogoBounds({ data, width, height }) {
  const isWhite = (offset) => data[offset + 3] >= 250 && data[offset] >= 245 && data[offset + 1] >= 245 && data[offset + 2] >= 245;
  let borderCount = 0;
  let whiteCount = 0;
  const sample = (x, y) => {
    borderCount++;
    if (isWhite((y * width + x) * 4)) whiteCount++;
  };
  for (let x = 0; x < width; x++) { sample(x, 0); sample(x, height - 1); }
  for (let y = 1; y < height - 1; y++) { sample(0, y); sample(width - 1, y); }
  const whiteBackground = whiteCount / borderCount >= 0.995;
  let left = width, top = height, right = -1, bottom = -1;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const offset = (y * width + x) * 4;
      if (data[offset + 3] < 8 || (whiteBackground && isWhite(offset))) continue;
      left = Math.min(left, x); right = Math.max(right, x);
      top = Math.min(top, y); bottom = Math.max(bottom, y);
    }
  }
  if (right < left || bottom < top) return null;
  const padding = Math.max(2, Math.ceil(Math.max(right - left + 1, bottom - top + 1) * 0.015));
  left = Math.max(0, left - padding); top = Math.max(0, top - padding);
  right = Math.min(width, right + 1 + padding); bottom = Math.min(height, bottom + 1 + padding);
  const cropWidth = right - left, cropHeight = bottom - top;
  return { x: left / width, y: top / height, width: cropWidth / width, height: cropHeight / height, ratio: cropWidth / cropHeight };
}

function measure(source, sourceRatio) {
  const key = `${source}|${sourceRatio}`;
  if (measurements.has(key)) return measurements.get(key);
  const pending = new Promise((resolve) => {
    const probe = new Image();
    probe.crossOrigin = 'anonymous';
    probe.decoding = 'async';
    probe.onload = () => {
      try {
        const ratio = sourceRatio > 0 ? sourceRatio : probe.naturalWidth / probe.naturalHeight;
        if (!Number.isFinite(ratio) || ratio <= 0) return resolve(null);
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(ratio >= 1 ? 512 : 512 * ratio));
        canvas.height = Math.max(1, Math.round(ratio >= 1 ? 512 / ratio : 512));
        const context = canvas.getContext('2d', { willReadFrequently: true });
        if (!context) return resolve(null);
        context.drawImage(probe, 0, 0, canvas.width, canvas.height);
        const bounds = findLogoBounds(context.getImageData(0, 0, canvas.width, canvas.height));
        if (bounds) bounds.ratio = ratio * bounds.width / bounds.height;
        resolve(bounds);
      } catch { resolve(null); }
    };
    probe.onerror = () => resolve(null);
    probe.src = source;
  });
  measurements.set(key, pending);
  return pending;
}

class PDPBrandLogo extends HTMLElement {
  connectedCallback() {
    this.mobile = matchMedia('(max-width: 749px)');
    this.onViewportChange = () => { if (this.mobile.matches) this.normalize(); };
    this.mobile.addEventListener('change', this.onViewportChange);
    this.onViewportChange();
  }

  disconnectedCallback() {
    this.mobile?.removeEventListener('change', this.onViewportChange);
  }

  async normalize() {
    if (this.dataset.normalized || this.dataset.measuring || !this.dataset.logoSource) return;
    this.dataset.measuring = 'true';
    const bounds = await measure(this.dataset.logoSource, Number(this.dataset.logoRatio));
    delete this.dataset.measuring;
    if (!this.isConnected || !bounds) return;
    this.style.setProperty('--logo-ink-ratio', bounds.ratio);
    this.style.setProperty('--logo-image-width', `${100 / bounds.width}%`);
    this.style.setProperty('--logo-image-height', `${100 / bounds.height}%`);
    this.style.setProperty('--logo-image-left', `${-100 * bounds.x / bounds.width}%`);
    this.style.setProperty('--logo-image-top', `${-100 * bounds.y / bounds.height}%`);
    this.dataset.normalized = 'true';
    this.dispatchEvent(new CustomEvent('logo:normalized', { detail: bounds }));
  }
}

if (!customElements.get('pdp-brand-logo')) customElements.define('pdp-brand-logo', PDPBrandLogo);
